"use client";

import { ComponentPropsWithoutRef, FC, useEffect } from "react";
import { useMessages } from "../context/i18n";
import { common as enCommon } from "../dictionaries/en/common";
import { trial as enTrial } from "../dictionaries/en/trial";
import BenefitBlock, { BenefitBlockData } from "./blocks/BenefitBlock";
import FaqBlock, { FaqBlockData } from "./blocks/FaqBlock";
import Footer from "./Footer";
import Header from "./Header";
import PageHero from "./PageHero";
import { TaskPreset } from "../helpers/submitVideoDefaults";

import { SECTION_LG_MAX_W, SECTION_MD_MAX_W } from "../const/twConst";
import { WEB_ASSET_DEMO_MEDIA } from "../helpers/media-landing";
import FeaturesBlock, { FeaturesBlockData } from "./blocks/FeaturesBlock";
import HowToBlock, { HowToBlockData } from "./blocks/HowToBlock";
import MediaBlock from "./blocks/MediaBlock";
import Breadcrumb, { BreadcrumbItem } from "./Breadcrumb";

export type ToolPageData = {
  meta: {
    title: string;
    description: string;
  };
  label: string;
  title: string;
  description: string;
  cta: string;
  benefits: BenefitBlockData[];
  howTo: HowToBlockData;
  features: FeaturesBlockData;
  faq: FaqBlockData;
  taskPreset: TaskPreset;
  // A translated page's trail: the one derived from its URL would be English.
  breadcrumbItems?: BreadcrumbItem[];
};

type ToolPageProps = {
  data: ToolPageData;
} & ComponentPropsWithoutRef<"div">;

const ToolPage: FC<ToolPageProps> = ({ data, ...rest }) => {
  const m = useMessages("common", enCommon);
  const trial = useMessages("trial", enTrial);

  // Push the tool's preset onto the URL via `history.replaceState` so it's
  // available to downstream consumers (the upload form reads `?task=…`)
  // without forcing the page out of static rendering. Using
  // `useSearchParams()` / `useQueryParam` here would opt the route into
  // dynamic rendering and trigger Suspense fallbacks, both of which we
  // explicitly avoid for marketing routes targeting Lighthouse 100.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("task") === data.taskPreset) return;
    url.searchParams.set("task", data.taskPreset);
    window.history.replaceState(null, "", url.toString());
  }, [data.taskPreset]);

  return (
    <div {...rest} className={`${rest.className}`}>
      <Header />
      <Breadcrumb className="justify-center" items={data.breadcrumbItems} />
      <PageHero
        className="max-w-5xl mx-auto px-3 my-16"
        label={data.label}
        heroTitle={data.title}
        heroSubtitle={data.description}
        ctaLabel={data.cta}
        caption={trial.cta}
      />
      <div className={`px-3 ${SECTION_LG_MAX_W} mx-auto my-32`}>
        <MediaBlock
          className="shadow-2xl shadow-slate-300 ring-2 ring-slate-300/50 bg-slate-200 mx-auto"
          data={{
            type: "video",
            src: WEB_ASSET_DEMO_MEDIA.beforeAfter.src,
            alt: m.demoAlt,
          }}
        />
      </div>
      {data.benefits.map((benefit) => (
        <BenefitBlock
          key={benefit.title}
          data={benefit}
          className={SECTION_LG_MAX_W}
        />
      ))}
      <HowToBlock data={data.howTo} className={SECTION_LG_MAX_W} />
      <FeaturesBlock data={data.features} className={SECTION_LG_MAX_W} />
      <FaqBlock data={data.faq} className={SECTION_MD_MAX_W} />
      <Footer />
    </div>
  );
};

export default ToolPage;
